import { verifyInterface } from "../../../utils";
import { AssignScore } from "../utils";
import { Sound } from "../../../sound";
import {
  AwayHandler,
  REQUIRED_MP3_VALUES,
  TriggeredAction,
} from "../awayHandler";

class AssignAwayHandler extends AwayHandler {
  constructor({ controllerCtxt, sound }) {
    const actionToTrigger = (turboBeforeVisitEvent) => {
      if (turboBeforeVisitEvent) {
        // urls are hardcoded - if this breaks in an unexpected way, the next beer is on me.
        const targetUrl = new URL(turboBeforeVisitEvent.detail.url);
        if (targetUrl.pathname.includes("/terminal/assign/employee")) {
          return TriggeredAction.ToEmployee;
        }
        if (targetUrl.pathname.includes("/terminal/assign/store")) {
          return TriggeredAction.None;
        }
        if (targetUrl.pathname.includes("/terminal/assign/success")) {
          return TriggeredAction.ToStore;
        }
      }
      return TriggeredAction.Cancel;
    };

    super({ actionToTrigger, controllerCtxt, sound });
  }
}

export class AssignStore {
  requiredInterface = REQUIRED_MP3_VALUES;

  constructor(controllerCtxt) {
    this.ctrl = controllerCtxt;
    verifyInterface(this.ctrl, {
      requiredInterface: this.requiredInterface,
      checkCSRF: true,
    });
    this.handledDetects = window.terminal.handledDetects;
    this.sound = new Sound({
      cancel: this.ctrl.cancelMp3Value,
      error: this.ctrl.errorMp3Value,
      toStore: this.ctrl.toStoreMp3Value,
      toEmployee: this.ctrl.toEmployeeMp3Value,
    });

    this.awayHandler = new AssignAwayHandler({
      controllerCtxt: this.ctrl,
      sound: this.sound,
    });
    this.awayHandler.registerNavigateAwayWarn();

    this.setAssignedAssetsInputs();
  }

  deConstructor() {
    this.awayHandler.unregisterNavigateAwayWarn();
    window.terminal.handledDetects = this.handledDetects;
  }

  handleEvent(event) {
    switch (event.action) {
      default:
        console.error("unsupported event for mode 'AssignStore'", event);
    }
  }

  getAssigned() {
    return Object.entries(this.handledDetects).filter(([_, s]) =>
      AssignScore.isAssigned(s),
    );
  }

  unassignManually(event) {
    const assetId = event.params.assetId;
    this.sound.playOnce("cancel").then();
    event.target.closest("div.asset-tile").remove();
    this.handledDetects[assetId] = AssignScore.ManuallyUnassigned;
    this.setAssignedAssetsInputs();
  }

  setAssignedAssetsInputs() {
    const idList = Object.entries(this.handledDetects)
      .filter(([_, score]) => AssignScore.isAssigned(score))
      .map(([assetId, _]) => assetId);
    const disabled = idList.length === 0;
    const idListStr = idList.join(",");

    for (const idsInput of this.ctrl.assignedAssetsInputTargets) {
      idsInput.value = idListStr;
      const submit = idsInput.form.querySelector("[type=submit]");
      if (submit) {
        submit.disabled = disabled;
      }
    }
  }
}

export class AssignEmployee {
  requiredInterface = ["goingAwayModalTarget", "employeeIdInputTarget"].concat(
    REQUIRED_MP3_VALUES,
  );

  constructor(controllerCtxt) {
    this.ctrl = controllerCtxt;
    verifyInterface(this.ctrl, { requiredInterface: this.requiredInterface });

    if (this.isAssetAssignment()) {
      this.awayHandler = new AssignAwayHandler({
        controllerCtxt: this.ctrl,
        sound: new Sound({
          error: this.ctrl.errorMp3Value,
          cancel: this.ctrl.cancelMp3Value,
          toStore: this.ctrl.toStoreMp3Value,
          toEmployee: this.ctrl.toEmployeeMp3Value,
        }),
      });
      this.awayHandler.registerNavigateAwayWarn();
    }
  }

  isAssetAssignment() {
    const params = new URLSearchParams(document.location.search);
    const asset_id_list = params.get("asset_id_list");
    return asset_id_list !== null;
  }

  deConstructor() {
    if (this.awayHandler) {
      this.awayHandler.unregisterNavigateAwayWarn();
    }
  }

  handleEvent(event) {
    switch (event.action) {
      case "employeeDetected":
        this.handleEmployeeDetected(event.id);
        break;
      default:
        console.error("unsupported event for mode 'AssignEmployee'", event);
    }
  }

  handleEmployeeDetected(employeeId) {
    const idInput = this.ctrl.employeeIdInputTarget;
    idInput.value = employeeId;
    idInput.form.requestSubmit();
  }
}
